import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Phone, Mail, MapPin, Globe, Navigation,
  Upload, FileText, CheckCircle, Loader2, ShoppingCart, X
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PlaceOrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    email: '',
    address: '',
    officeName: '',
    city: '',
    state: '',
    country: 'India',
    pinCode: '',
    selectedService: location.state?.selectedService || '',
    selectedPlan: '',
    latitude: '',
    longitude: '',
    additionalNotes: ''
  });

  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [fetchingPincode, setFetchingPincode] = useState(false);

  const services = [
    'Internet Leased Line',
    'Broadband Services',
    'OTT Services',
    'VoIP Services',
    'CCTV Installation',
    'Network Setup',
    'WiFi Solutions',
    'Other Services'
  ];

  const servicePlans = {
    'Internet Leased Line': ['5 Mbps', '10 Mbps', '50 Mbps', '100 Mbps', '500 Mbps', '1 Gbps', '10 Gbps'],
    'Broadband Services': ['5 Mbps', '50 Mbps', '100 Mbps', '200 Mbps', '500 Mbps', '1 Gbps'],
    'OTT Services': ['5 Mbps', '50 Mbps', '100 Mbps', '200 Mbps'],
    'VoIP Services': ['Basic Plan', 'Business Plan', 'Enterprise Plan'],
    'CCTV Installation': ['2 Cameras', '4 Cameras', '8 Cameras', '16 Cameras', 'Custom'],
    'Network Setup': ['Small Office', 'Medium Office', 'Large Office', 'Enterprise'],
    'WiFi Solutions': ['Home WiFi', 'Office WiFi', 'Mesh Network', 'Enterprise WiFi'],
    'Other Services': ['Custom Plan']
  };

  const availablePlans = formData.selectedService ? servicePlans[formData.selectedService] || [] : [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-fetch city and state when pin code is complete (6 digits for India)
    if (name === 'pinCode' && value.length === 6) {
      fetchCityStateFromPinCode(value);
    }
  };

  const fetchCityStateFromPinCode = async (pinCode) => {
    try {
      setFetchingPincode(true);
      const response = await fetch(`https://api.postalpincode.in/pincode/${pinCode}`);
      const data = await response.json();

      if (data && data[0]?.Status === 'Success' && data[0].PostOffice?.length > 0) {
        const postOffice = data[0].PostOffice[0];
        setFormData(prev => ({
          ...prev,
          city: postOffice.District || postOffice.Block,
          state: postOffice.State,
          country: postOffice.Country
        }));
        toast.success('City and State auto-filled!');
      } else {
        toast.error('Invalid PIN code or no data found');
      }
    } catch (error) {
      console.error('Error fetching pin code data:', error);
      toast.error('Failed to fetch location details');
    } finally {
      setFetchingPincode(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({
          ...prev,
          latitude: latitude.toFixed(6),
          longitude: longitude.toFixed(6)
        }));
        toast.success('Location captured successfully!');
        setFetchingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Failed to get current location. Please enable location access.');
        setFetchingLocation(false);
      }
    );
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];

    for (const file of files) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        continue;
      }

      validFiles.push(file);
    }

    setAttachments([...attachments, ...validFiles]);
    if (validFiles.length > 0) {
      toast.success(`${validFiles.length} file(s) added`);
    }
  };

  const removeAttachment = (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
    toast.info('File removed');
  };

  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;
    const pinCodeRegex = /^[0-9]{6}$/;

    if (!formData.fullName.trim()) {
      toast.error('Please enter your full name');
      return false;
    }

    if (!phoneRegex.test(formData.mobileNumber)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return false;
    }

    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (!formData.address.trim()) {
      toast.error('Please enter your address');
      return false;
    }

    if (!pinCodeRegex.test(formData.pinCode)) {
      toast.error('Please enter a valid 6-digit PIN code');
      return false;
    }

    if (!formData.city.trim() || !formData.state.trim()) {
      toast.error('City and State are required. Please enter PIN code to auto-fetch.');
      return false;
    }

    if (!formData.selectedService) {
      toast.error('Please select a service');
      return false;
    }

    if (!formData.selectedPlan) {
      toast.error('Please select a plan');
      return false;
    }

    if (!formData.latitude || !formData.longitude) {
      toast.error('Please provide location coordinates or use current location');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Append files
      attachments.forEach((file, index) => {
        formDataToSend.append('attachments', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/orders/submit`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Order submitted successfully! Confirmation email sent.');
        
        // Navigate to success page or home
        setTimeout(() => {
          navigate('/order-success', { state: { orderData: data } });
        }, 2000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit order');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-5xl mx-auto px-6">
          <Button
            onClick={() => navigate('/request-service')}
            variant="outline"
            className="mb-6 bg-white/20 hover:bg-white/30 text-white border-white/40 backdrop-blur-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Services
          </Button>
          
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-white/20 backdrop-blur-md p-4 rounded-full border-2 border-white/40">
                <ShoppingCart className="h-12 w-12" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-3 drop-shadow-lg">Place Your Order</h1>
            <p className="text-xl text-white/90">
              Fill in your details and we'll get back to you within 24 hours
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Personal Information */}
              <Card className="border-4 border-blue-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-500 text-white">
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-6 w-6" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="mobileNumber">Mobile Number *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="mobileNumber"
                          name="mobileNumber"
                          type="tel"
                          value={formData.mobileNumber}
                          onChange={handleInputChange}
                          placeholder="10-digit mobile number"
                          className="pl-10"
                          maxLength={10}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your.email@example.com"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Address Information */}
              <Card className="border-4 border-green-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-green-600 to-green-500 text-white">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-6 w-6" />
                    Installation Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <Label htmlFor="address">Full Address *</Label>
                    <Textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="House No., Street, Landmark"
                      rows={3}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="officeName">Office Name</Label>
                    <Input
                      id="officeName"
                      name="officeName"
                      value={formData.officeName}
                      onChange={handleInputChange}
                      placeholder="Enter office/building name (optional)"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pinCode">PIN Code *</Label>
                      <Input
                        id="pinCode"
                        name="pinCode"
                        type="text"
                        value={formData.pinCode}
                        onChange={handleInputChange}
                        placeholder="6-digit PIN code"
                        maxLength={6}
                        required
                      />
                      {fetchingPincode && (
                        <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Fetching location...
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Auto-filled from PIN code"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="Auto-filled from PIN code"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Service Selection & Location */}
              <Card className="border-4 border-purple-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-500 text-white">
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-6 w-6" />
                    Service & Location Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <Label htmlFor="selectedService">Select Service *</Label>
                    <select
                      id="selectedService"
                      name="selectedService"
                      value={formData.selectedService}
                      onChange={(e) => {
                        handleInputChange(e);
                        // Reset plan when service changes
                        setFormData(prev => ({ ...prev, selectedPlan: '' }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    >
                      <option value="">-- Select a Service --</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.selectedService && availablePlans.length > 0 && (
                    <div>
                      <Label htmlFor="selectedPlan">Select Plan *</Label>
                      <select
                        id="selectedPlan"
                        name="selectedPlan"
                        value={formData.selectedPlan}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      >
                        <option value="">-- Select a Plan --</option>
                        {availablePlans.map((plan) => (
                          <option key={plan} value={plan}>
                            {plan}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="latitude">Latitude *</Label>
                      <Input
                        id="latitude"
                        name="latitude"
                        type="text"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        placeholder="e.g., 28.7041"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="longitude">Longitude *</Label>
                      <Input
                        id="longitude"
                        name="longitude"
                        type="text"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        placeholder="e.g., 77.1025"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleUseCurrentLocation}
                    disabled={fetchingLocation}
                    variant="outline"
                    className="w-full border-2 border-purple-500 hover:bg-purple-50"
                  >
                    {fetchingLocation ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Getting Location...
                      </>
                    ) : (
                      <>
                        <Navigation className="h-4 w-4 mr-2" />
                        Use Current Location
                      </>
                    )}
                  </Button>

                  <div>
                    <Label htmlFor="additionalNotes">Additional Notes (Optional)</Label>
                    <Textarea
                      id="additionalNotes"
                      name="additionalNotes"
                      value={formData.additionalNotes}
                      onChange={handleInputChange}
                      placeholder="Any specific requirements or instructions"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Attachments */}
              <Card className="border-4 border-orange-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-orange-600 to-orange-500 text-white">
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-6 w-6" />
                    Documents (Optional)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      className="w-full border-2 border-dashed border-orange-400 hover:bg-orange-50"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Documents
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      className="hidden"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Supported: PDF, DOC, DOCX, JPG, PNG (Max 10MB per file)
                    </p>
                  </div>

                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      {attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-orange-600" />
                            <span className="text-sm font-medium">{file.name}</span>
                            <span className="text-xs text-gray-500">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttachment(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Summary & Submit */}
            <div className="lg:col-span-1">
              <Card className="border-4 border-yellow-200 shadow-xl sticky top-6">
                <CardHeader className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-white">
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-3">
                    {formData.fullName && (
                      <div>
                        <p className="text-xs text-gray-500">Customer Name</p>
                        <p className="font-semibold">{formData.fullName}</p>
                      </div>
                    )}

                    {formData.selectedService && (
                      <div>
                        <p className="text-xs text-gray-500">Selected Service</p>
                        <p className="font-semibold text-purple-700">{formData.selectedService}</p>
                        {formData.selectedPlan && (
                          <p className="text-sm text-purple-600 mt-1">Plan: {formData.selectedPlan}</p>
                        )}
                      </div>
                    )}

                    {formData.city && formData.state && (
                      <div>
                        <p className="text-xs text-gray-500">Location</p>
                        <p className="font-semibold">{formData.city}, {formData.state}</p>
                      </div>
                    )}

                    {attachments.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500">Attachments</p>
                        <p className="font-semibold">{attachments.length} file(s)</p>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-blue-900 font-semibold mb-2">What happens next?</p>
                      <ul className="text-xs text-blue-800 space-y-1">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span>You'll receive a confirmation email</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span>Our team will contact you within 24 hours</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span>Installation scheduled at your convenience</span>
                        </li>
                      </ul>
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-14 text-lg font-bold bg-gradient-to-r from-green-600 to-green-700 hover:shadow-xl"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Submitting Order...
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="h-5 w-5 mr-2" />
                          Submit Order
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlaceOrderForm;
